import { useSnapDebugStore } from '@utils/modularSnap/snapDebugStore'
import { Html, Line } from '@react-three/drei'
import { useAppStore } from '@store'
import { useMemo } from 'react'
import { Euler, Matrix4, Quaternion, Vector3 } from 'three'

const VALID_COLOR = '#22c55e'
const INVALID_COLOR = '#ef4444'
const BEST_COLOR = '#eab308'

function ConnectorArrow({
  position,
  quaternion,
  color,
  length = 220,
}: {
  position: Vector3
  quaternion: Quaternion
  color: string
  length?: number
}) {
  const points = useMemo(() => {
    const origin = position.clone()
    origin.y += 30
    const dir = new Vector3(1, 0, 0).applyQuaternion(quaternion).multiplyScalar(length)
    const tip = origin.clone().add(dir)
    return [origin, tip]
  }, [
    position.x,
    position.y,
    position.z,
    quaternion.x,
    quaternion.y,
    quaternion.z,
    quaternion.w,
    length,
  ])

  return (
    <Line
      points={points}
      color={color}
      lineWidth={3}
      depthTest={false}
      transparent
      opacity={0.9}
    />
  )
}

function SnapReachBox({
  position,
  quaternion,
  widthMm,
  depthMm,
  color,
}: {
  position: Vector3
  quaternion: Quaternion
  widthMm: number
  depthMm: number
  color: string
}) {
  const corners = useMemo(() => {
    const hw = widthMm / 2
    const hd = depthMm / 2
    const local = [
      new Vector3(-hw, 0, -hd),
      new Vector3(hw, 0, -hd),
      new Vector3(hw, 0, hd),
      new Vector3(-hw, 0, hd),
      new Vector3(-hw, 0, -hd),
    ]
    const m = new Matrix4().compose(position, quaternion, new Vector3(1, 1, 1))
    return local.map((p) => p.applyMatrix4(m))
  }, [position, quaternion, widthMm, depthMm])

  return (
    <Line
      points={corners}
      color={color}
      lineWidth={1.5}
      depthTest={false}
      transparent
      opacity={0.55}
    />
  )
}

function SnapMarker({
  position,
  color,
  label,
}: {
  position: Vector3
  color: string
  label: string
}) {
  const points = useMemo(
    () => [
      new Vector3(position.x, position.y + 40, position.z),
      new Vector3(position.x, position.y + 220, position.z),
    ],
    [position.x, position.y, position.z],
  )
  return (
    <group>
      <Line
        points={points}
        color={color}
        lineWidth={2}
        depthTest={false}
        transparent
        opacity={0.95}
      />
      <mesh position={[position.x, position.y + 12, position.z]}>
        <sphereGeometry args={[18, 12, 12]} />
        <meshBasicMaterial
          color={color}
          depthTest={false}
          transparent
          opacity={0.85}
        />
      </mesh>
      <Html
        position={[position.x, position.y + 240, position.z]}
        center
        style={{
          pointerEvents: 'none',
          background: 'rgba(0,0,0,0.72)',
          color: '#fff',
          padding: '4px 8px',
          borderRadius: 4,
          fontSize: 11,
          whiteSpace: 'nowrap',
          border: `1px solid ${color}`,
        }}
      >
        {label}
      </Html>
    </group>
  )
}

export function ModularSnapDebug3D() {
  const enabled = useSnapDebugStore((s) => s.enabled)
  const candidates = useSnapDebugStore((s) => s.candidates)
  const best = useSnapDebugStore((s) => s.best)
  const dragPosition = useSnapDebugStore((s) => s.dragPosition)

  if (!enabled) return null

  return (
    <group name='modular-snap-debug'>
      {dragPosition && (
        <SnapMarker
          position={dragPosition}
          color='#60a5fa'
          label='drag'
        />
      )}
      {best?.valid && (
        <>
          <ConnectorArrow
            position={best.position}
            quaternion={best.quaternion}
            color={BEST_COLOR}
            length={280}
          />
          <SnapReachBox
            position={best.position}
            quaternion={best.quaternion}
            widthMm={600}
            depthMm={600}
            color={BEST_COLOR}
          />
        </>
      )}
      {candidates.slice(0, 8).map((c, i) => {
        const isBest = best && String(best.targetId) === String(c.targetId)
        const color = isBest ? BEST_COLOR : c.valid ? VALID_COLOR : INVALID_COLOR
        const rotY =
          ((c.quaternion.w ? new Euler().setFromQuaternion(c.quaternion).y : 0) * 180) / Math.PI
        return (
          <group key={`${c.targetId}-${c.connector}-${i}`}>
            <SnapMarker
              position={c.position}
              color={color}
              label={`${c.connector} ${Math.round(c.distanceMm)}mm ${c.valid ? 'OK' : c.reason}${c.autoRotationApplied ? ' · rot' : ''} rotY≈${rotY.toFixed(0)}°`}
            />
            {c.valid && (
              <ConnectorArrow
                position={c.position}
                quaternion={c.quaternion}
                color={color}
                length={160}
              />
            )}
          </group>
        )
      })}
    </group>
  )
}

/** HUD 2D sobre el canvas (activar con window.__enableModularSnapDebug__()). */
export function SnapDebugHud() {
  const enabled = useSnapDebugStore((s) => s.enabled)
  const best = useSnapDebugStore((s) => s.best)
  const candidates = useSnapDebugStore((s) => s.candidates)
  const dragId = useSnapDebugStore((s) => s.dragId)
  const mode = useAppStore.use.mode()

  if (!enabled) return null

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 12,
        left: 12,
        zIndex: 9999,
        background: 'rgba(15,23,42,0.92)',
        color: '#e2e8f0',
        padding: '10px 12px',
        borderRadius: 8,
        fontFamily: 'ui-monospace, monospace',
        fontSize: 11,
        maxWidth: 420,
        pointerEvents: 'none',
        border: '1px solid #334155',
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6, color: '#fbbf24' }}>Modular Snap Debug</div>
      <div>mode: {mode}</div>
      <div>drag: {dragId ?? '—'}</div>
      <div>candidates: {candidates.length}</div>
      {best ? (
        <div style={{ marginTop: 6, color: best.valid ? VALID_COLOR : INVALID_COLOR }}>
          best → {String(best.targetId)} · {best.connector} · {Math.round(best.distanceMm)}mm ·{' '}
          {best.reason}
          {best.autoRotationApplied ? ' · auto-rot' : ''}
        </div>
      ) : (
        <div style={{ marginTop: 6, color: '#94a3b8' }}>best → none</div>
      )}
    </div>
  )
}
